import dayjs from 'dayjs';
import { fromEnumAsString } from '../../../serialization/enum-utils.js';
import { TimeZone } from '../../../time/timezone.js';
import { DayOfYear } from '../day-of-year.js';
import { TimeZoneRegion } from '../time-zone-region.js';
import { CalendarDayState } from './calendar-day-state.js';
import { isNullOrUndefined } from '../../../utility/null-safety.js';
import { CalendarSnapshot } from './calendar-snapshot.js';
import { IDailyCalendarSnapshot } from '../../../contracts/daily-calendar-snapshot.js';
import { ICloneable } from '../../../contracts/cloneable.js';
import { IDataComparable } from '../../../contracts/data-comparable.js';
import { IDiagnosticableObject } from '../../../contracts/diagnosticable-object.js';
import {
  cloneMap,
  isSameData,
  isSameMap,
  SerializedData,
} from '../../dto/dto.js';

export class DailyCalendarSnapshot implements ICloneable,
                                              IDataComparable,
                                              IDailyCalendarSnapshot,
                                              IDiagnosticableObject {
  public readonly date: dayjs.Dayjs;

  constructor(
    public readonly dayOfYear: DayOfYear,
    public readonly timezone: TimeZoneRegion,
    public readonly calendars: Map<string, CalendarDayState>,
  ) {
    this.date = this.dayOfYear.toDate(TimeZone.ofRegion(this.timezone));
  }

  get dayOfMonth(): number {
    return this.dayOfYear.day;
  }

  static fromOther(dailyCalendar: IDailyCalendarSnapshot): DailyCalendarSnapshot {
    return new DailyCalendarSnapshot(
        dailyCalendar.dayOfYear.clone(),
        dailyCalendar.timezone,
        cloneMap(dailyCalendar.calendars, (cs) => cs.clone()),
    );
  }

  static empty(dayOfYear: DayOfYear, timezone: TimeZoneRegion): DailyCalendarSnapshot {
    return new DailyCalendarSnapshot(
        dayOfYear,
        timezone,
        new Map<string, CalendarDayState>(),
    );
  }

  clone(): this {
    return new DailyCalendarSnapshot(
        this.dayOfYear.clone(),
        this.timezone,
        cloneMap(this.calendars, (cs) => cs.clone()),
    ) as this;
  }

  isSame(other: this): boolean {
    if(other === this) {
      return true;
    }

    return isSameMap(other.calendars, this.calendars, (a, b) => isSameData(a, b)) &&
        isSameData(other.dayOfYear, this.dayOfYear) &&
        other.timezone === this.timezone;
  }

  getCalendarState(uniqueId?: string): CalendarDayState | null {
    return this.calendars.get(uniqueId ?? CalendarSnapshot.GLOBAL_STATE_ID) ?? null;
  }

  getOrCreateCalendarState(uniqueId: string): CalendarDayState {
    let calendarState = this.calendars.get(uniqueId ?? CalendarSnapshot.GLOBAL_STATE_ID) ?? null;
    if(isNullOrUndefined(calendarState)) {
      calendarState = CalendarDayState.empty(uniqueId);
      this.calendars.set(uniqueId, calendarState);
    }

    return calendarState;
  }

  toDiagnosticsObject(): SerializedData {
    const calendars: SerializedData = {};
    this.calendars.forEach((v, k) => {
      calendars[k] = v.toDiagnosticsObject();
    });

    return {
      'dayOfYear': this.dayOfYear.uniqueId,
      'date': TimeZone.encodeDate(this.date),
      'timezone': fromEnumAsString(this.timezone),
      'calendars': calendars,
    };
  }
}
